// src/features/monetize/components/SettingsTab.tsx
//
// The storefront's name and support address (what viewer e-mails and the
// paywall carry), payouts through the operator's own Stripe account, and
// the platform fee that applies to each sale.

import { useEffect } from 'react';
import { ActionIcon, Alert, Badge, Button, Card, Code, CopyButton, Group, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconExternalLink, IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { useController, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { MONETIZE_CONSTRAINTS } from '@/lib/constants/monetize';
import type { MonetizeStatus } from '@/lib/types/Monetize';
import { MonetizeUtils } from '../MonetizeUtils';
import { mailSenderFormSchema, profileFormSchema, type MailSenderFormValues, type ProfileFormValues } from '../schemas/monetizeSchemas';
import { useRefreshConnect, useSaveProfile, useSetMailSender, useStartOnboarding, useVerifyMailSender } from '../useMonetizeMutations';

interface SettingsTabProps {
  status: MonetizeStatus;
}

export function SettingsTab({ status }: SettingsTabProps) {
  const profile = status.profile ?? null;
  return (
    <Stack gap="md" maw={760}>
      {!profile && (
        <Alert color="blue" icon={<IconInfoCircle size={16} />} title="Start here">
          Name your storefront and give viewers a support address. Everything else unlocks once this is saved.
        </Alert>
      )}
      <ProfileCard status={status} />
      {profile && <PayoutsCard status={status} />}
      {profile && <MailSenderCard status={status} />}
      {profile && (
        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="xs">
            <Text fw={600}>Platform fee</Text>
            <Text size="sm">
              {MonetizeUtils.feeLabel(profile.feeBps)} of each sale, collected automatically with the payment. Card processing fees are the payment provider's and appear in your own Stripe dashboard.
            </Text>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

function ProfileCard({ status }: { status: MonetizeStatus }) {
  const { selectedCustomer } = useAuth();
  const isReseller = useResellerBrand();
  const save = useSaveProfile();
  const profile = status.profile ?? null;
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: { brandName: profile?.brandName ?? selectedCustomer?.cfg.label ?? '', supportEmail: profile?.supportEmail ?? '' },
  });

  useEffect(() => {
    form.reset({ brandName: profile?.brandName ?? selectedCustomer?.cfg.label ?? '', supportEmail: profile?.supportEmail ?? '' });
  }, [profile, selectedCustomer, form]);

  async function handleSubmit(v: ProfileFormValues) {
    try {
      await save.mutateAsync({ brandName: v.brandName.trim(), supportEmail: v.supportEmail.trim(), reseller: isReseller });
      notifications.show({ title: 'Saved', message: 'Your storefront details are saved.', color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Failed to save.'), color: 'red' });
    }
  }

  return (
    <Card shadow="sm" padding="lg" withBorder>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text fw={600}>Storefront</Text>
          <BrandNameField control={form.control} />
          <SupportEmailField control={form.control} />
          <Group justify="flex-end">
            <Button type="submit" loading={save.isPending} disabled={!form.formState.isDirty && !!profile}>
              {profile ? 'Save changes' : 'Save and continue'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

function BrandNameField({ control }: { control: Control<ProfileFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'brandName' });
  return <TextInput label="Name viewers see" withAsterisk description="On the sign-in and purchase screens, receipts and e-mails" maxLength={MONETIZE_CONSTRAINTS.NAME_MAX} value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />;
}

function SupportEmailField({ control }: { control: Control<ProfileFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'supportEmail' });
  return <TextInput label="Support e-mail" withAsterisk description="Where a viewer's reply to a receipt or sign-in e-mail goes" type="email" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />;
}

function PayoutsCard({ status }: { status: MonetizeStatus }) {
  const onboarding = useStartOnboarding();
  const refresh = useRefreshConnect();
  const account = status.connect?.account ?? null;
  const profile = status.profile!;

  async function start() {
    const here = `${window.location.origin}/monetize?tab=settings`;
    try {
      const res = await onboarding.mutateAsync({
        returnUrl: `${here}&payouts=done`,
        refreshUrl: `${here}&payouts=refresh`,
        displayName: profile.brandName,
        contactEmail: profile.supportEmail,
        country: 'US',
      });
      window.location.assign(res.url);
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not start the payouts setup.'), color: 'red' });
    }
  }

  async function check() {
    try {
      const res = await refresh.mutateAsync();
      notifications.show({ title: 'Checked', message: res.account?.canSell ? 'Payouts are ready.' : 'Setup is not finished yet.', color: res.account?.canSell ? 'green' : 'blue' });
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not check the payouts status.'), color: 'red' });
    }
  }

  return (
    <Card shadow="sm" padding="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600}>Payouts</Text>
          {account && (
            <Badge color={account.canSell ? 'green' : 'yellow'} variant="light">
              {account.canSell ? 'Ready to sell' : 'Setup in progress'}
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          Sales are paid into your own Stripe account; payouts, disputes and tax live there. Setup takes a few minutes and happens on Stripe's secure pages.
        </Text>
        {!account && (
          <Button leftSection={<IconExternalLink size={16} />} onClick={start} loading={onboarding.isPending} style={{ alignSelf: 'flex-start' }}>
            Set up payouts
          </Button>
        )}
        {account && !account.canSell && (
          <Group>
            <Button leftSection={<IconExternalLink size={16} />} onClick={start} loading={onboarding.isPending}>Finish setup</Button>
            <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={check} loading={refresh.isPending}>Check status</Button>
          </Group>
        )}
        {account?.canSell && (
          <Text size="xs" c="dimmed">Connected account {account.stripeAccountId}{account.livemode ? '' : ' (test mode)'}</Text>
        )}
      </Stack>
    </Card>
  );
}

function MailSenderCard({ status }: { status: MonetizeStatus }) {
  const profile = status.profile!;
  const sender = profile.mailSender ?? null;
  const set = useSetMailSender();
  const verify = useVerifyMailSender();
  const form = useForm<MailSenderFormValues>({
    resolver: zodResolver(mailSenderFormSchema),
    defaultValues: { fromLocal: sender?.fromLocal ?? 'no-reply', domain: sender?.domain ?? '' },
  });

  useEffect(() => {
    form.reset({ fromLocal: sender?.fromLocal ?? 'no-reply', domain: sender?.domain ?? '' });
  }, [sender, form]);

  async function save(v: MailSenderFormValues) {
    try {
      const p = await set.mutateAsync({ fromLocal: v.fromLocal, domain: v.domain });
      const changed = p.mailSender?.domain !== sender?.domain;
      notifications.show({ title: 'Sender saved', message: changed ? 'Add the two records at your DNS host, then click Verify.' : `E-mails will come from ${p.mailSender?.address ?? v.fromLocal + '@' + v.domain}.`, color: 'green' });
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not save the sender.'), color: 'red', autoClose: 10000 });
    }
  }

  async function check() {
    try {
      const p = await verify.mutateAsync();
      const ok = !!p.mailSender?.verified;
      notifications.show({
        title: ok ? 'Verified' : 'Not yet',
        message: ok ? `E-mails now come from ${p.mailSender?.address}.` : 'One or both records were not found. DNS changes can take up to an hour; check the host names match exactly.',
        color: ok ? 'green' : 'yellow',
        autoClose: ok ? 5000 : 10000,
      });
    } catch (err) {
      notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not check the records.'), color: 'red', autoClose: 10000 });
    }
  }

  function clear() {
    modals.openConfirmModal({
      title: 'Use the platform sender instead?',
      children: <Text size="sm">E-mails go back to the platform's address with your name on them. You can set your own domain again later.</Text>,
      labels: { confirm: 'Use the platform sender', cancel: 'Keep mine' },
      onConfirm: async () => {
        try {
          await set.mutateAsync({ fromLocal: '', domain: '' });
          notifications.show({ title: 'Sender cleared', message: 'E-mails come from the platform sender again.', color: 'green' });
        } catch (err) {
          notifications.show({ title: 'Error', message: MonetizeUtils.errorMessage(err, 'Could not clear the sender.'), color: 'red' });
        }
      },
    });
  }

  const badge = sender ? (sender.verified ? { color: 'green', label: 'Verified' } : { color: 'yellow', label: 'Waiting for DNS' }) : { color: 'gray', label: 'Platform sender' };

  return (
    <Card shadow="sm" padding="lg" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text fw={600}>E-mail sender</Text>
          <Badge color={badge.color} variant="light">{badge.label}</Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Sign-in codes, receipts and other viewer e-mails go out with your name on them{sender?.verified ? ` from ${sender.address}` : " from the platform's address"}. To send from your own domain, give an address you control and add two records at your DNS host.
        </Text>
        <form onSubmit={form.handleSubmit(save)}>
          <Group align="flex-start" grow>
            <MailLocalField control={form.control} />
            <MailDomainField control={form.control} />
          </Group>
          <Group justify="flex-end" mt="sm">
            {sender && <Button variant="subtle" color="gray" onClick={clear} disabled={set.isPending}>Use the platform sender instead</Button>}
            <Button type="submit" loading={set.isPending} disabled={!form.formState.isDirty && !!sender}>{sender ? 'Save changes' : 'Use this sender'}</Button>
          </Group>
        </form>
        {sender && (
          <Stack gap="xs">
            <Text size="sm">Add these two records at your DNS host (the names are exact; do not add your domain again to the host).</Text>
            <Table withTableBorder verticalSpacing="xs" style={{ tableLayout: 'fixed' }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={70}>Type</Table.Th>
                  <Table.Th>Host</Table.Th>
                  <Table.Th>Value</Table.Th>
                  <Table.Th w={90}>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <RecordRow type="TXT" host={sender.dkimHost} value={sender.dkimValue} ok={sender.dkimVerified} />
                <RecordRow type="CNAME" host={sender.returnPathHost} value={sender.returnPathValue} ok={sender.returnPathVerified} />
              </Table.Tbody>
            </Table>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{sender.checkedAt ? `Last checked ${MonetizeUtils.formatWhen(sender.checkedAt)}` : 'Not checked yet'}</Text>
              {!sender.verified && <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={check} loading={verify.isPending}>Verify</Button>}
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function RecordRow({ type, host, value, ok }: { type: string; host: string; value: string; ok: boolean }) {
  return (
    <Table.Tr>
      <Table.Td><Code>{type}</Code></Table.Td>
      <Table.Td><CopyCell value={host} /></Table.Td>
      <Table.Td><CopyCell value={value} /></Table.Td>
      <Table.Td><Badge size="sm" variant="light" color={ok ? 'green' : 'yellow'}>{ok ? 'Found' : 'Pending'}</Badge></Table.Td>
    </Table.Tr>
  );
}

function CopyCell({ value }: { value: string }) {
  return (
    <Group gap={4} wrap="nowrap">
      <Text size="xs" style={{ fontFamily: 'var(--mantine-font-family-monospace)', wordBreak: 'break-all' }}>{value || '—'}</Text>
      {value && (
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
              <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} size="sm" onClick={copy} aria-label="Copy">
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      )}
    </Group>
  );
}

function MailLocalField({ control }: { control: Control<MailSenderFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'fromLocal' });
  return <TextInput label="Address" withAsterisk description="The part before the @" placeholder="no-reply" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />;
}

function MailDomainField({ control }: { control: Control<MailSenderFormValues> }) {
  const { field, fieldState } = useController({ control, name: 'domain' });
  return <TextInput label="Domain" withAsterisk description="A domain you control, such as tv.example.com" placeholder="tv.example.com" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />;
}
